import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, CheckCircle } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { EmailGate } from "@/components/EmailGate";
import { useT } from "@/i18n";

const SAMPLE = "Nice apartment in Lisbon. 2 bedrooms, kitchen, wifi. Close to center. Good for families. Balcony. No parties. Check in after 3pm.";
const SAMPLE_IT = "Bell'appartamento a Napoli. 2 camere, cucina, wifi. Vicino al centro. Adatto alle famiglie. Balcone. No feste. Check-in dopo le 15.";
const SUBS = [["clarity", "Clarity"], ["appeal", "Appeal"], ["keywords", "Search keywords"], ["completeness", "Completeness"], ["trust", "Trust"]];

const ScoreRing = ({ score }) => {
  const r = 52, c = 2 * Math.PI * r, off = c - (c * (score || 0)) / 100;
  const color = score >= 75 ? "#0F4C5C" : score >= 50 ? "#C98A1B" : "#E36414";
  return (
    <svg width="128" height="128" viewBox="0 0 128 128" data-testid="grader-score-ring">
      <circle cx="64" cy="64" r={r} stroke="#E2DCD0" strokeWidth="10" fill="none" />
      <circle cx="64" cy="64" r={r} stroke={color} strokeWidth="10" fill="none" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 64 64)" style={{ transition: "stroke-dashoffset 1s ease" }} />
      <text x="64" y="70" textAnchor="middle" className="font-display" fontSize="34" fill="#0F4C5C" data-testid="grader-score-value">{score}</text>
      <text x="64" y="90" textAnchor="middle" fontSize="11" fill="#6b7280">/ 100</text>
    </svg>
  );
};

export const GraderWidget = ({ compact = false }) => {
  const [text, setText] = useState("");
  const { t, lang } = useT();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [full, setFull] = useState(null);

  const run = async () => {
    if (text.trim().length < 40) { toast.error(t("Paste at least 40 characters of your listing.")); return; }
    setBusy(true); setPreview(null); setFull(null);
    try {
      const { data } = await api.post("/tools/grade", { listing_text: text });
      setPreview(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-6 sm:p-8 bg-white" data-testid="grader-widget">
      <div className="flex items-center justify-between mb-3">
        <span className="label !mb-0">{t("Paste your listing description")}</span>
        <button onClick={() => setText(lang === "it" ? SAMPLE_IT : SAMPLE)} className="text-xs text-[#E36414] font-semibold hover:underline" data-testid="grader-sample-btn">{t("Use a sample")}</button>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={compact ? 5 : 8} className="field resize-none"
        placeholder={t("Copy the title and description from your listing and paste them here…")} data-testid="grader-input" />
      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs text-gray-500">{t("We never log in to Airbnb or Booking. You paste, we grade.")}</span>
        <button onClick={run} disabled={busy} className="btn-coral" data-testid="grader-submit-btn">
          <Sparkle size={18} weight="fill" /> {busy ? t("Grading…") : t("Grade my listing")}
        </button>
      </div>

      {busy && (
        <div className="mt-8 grid grid-cols-[128px_1fr] gap-6 items-center" data-testid="grader-loading">
          <div className="skeleton w-32 h-32 !rounded-full" />
          <div className="space-y-3"><div className="skeleton h-4 w-3/4" /><div className="skeleton h-4 w-2/3" /><div className="skeleton h-4 w-1/2" /></div>
        </div>
      )}

      {preview && (
        <div className="mt-8 rise" data-testid="grader-result">
          <div className="grid sm:grid-cols-[128px_1fr] gap-6 items-center">
            <ScoreRing score={preview.score} />
            <div>
              <p className="font-display text-xl text-[#0F4C5C]" data-testid="grader-summary">{preview.summary}</p>
              <p className="label mt-4">{t("Top 3 fixes")}</p>
              <ol className="space-y-2">
                {(full?.fixes || preview.fixes).map((f, i) => (
                  <li key={i} className="flex gap-3" data-testid={`grader-fix-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-[#0F4C5C] text-[#F4E9D8] text-xs grid place-items-center shrink-0">{i + 1}</span>
                    <div><p className="font-medium text-gray-900">{f.title}</p>{f.detail && <p className="text-sm text-gray-600">{f.detail}</p>}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          {!full ? (
            <div className="mt-6"><EmailGate reportId={preview.report_id} onUnlock={setFull} /></div>
          ) : (
            <div className="mt-6 grid gap-4" data-testid="grader-full-report">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {SUBS.map(([k, l]) => (
                  <div key={k} className="rounded-xl bg-[#F4E9D8] p-3" data-testid={`grader-sub-${k}`}>
                    <p className="text-xs text-gray-600">{t(l)}</p>
                    <p className="font-display text-2xl text-[#0F4C5C]">{full.subscores?.[k] ?? "–"}<span className="text-sm text-gray-500">/20</span></p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-[#E2DCD0] p-4 bg-[#FCFAF8]">
                <p className="label flex items-center gap-1"><CheckCircle size={14} weight="fill" /> {t("Example rewrite")}</p>
                <p className="font-display text-lg text-gray-900 italic" data-testid="grader-example-rewrite">“{full.example_rewrite}”</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
